import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'motion/react';
import { Wind, Sparkles, MessageCircle } from 'lucide-react';
import StarrySky from './StarrySky';

// A kind 404: for old or mistyped links. Always the night sky, a few drifting
// emojis, the three ways into Zuzu and the site footer (passed in by App, where
// it lives). Kept out of search (noindex).

const DRIFT = [
  { e: '💭', x: 12, y: 18, d: 0 }, { e: '🌙', x: 82, y: 14, d: 1.2 }, { e: '🤍', x: 70, y: 70, d: 0.6 },
  { e: '✨', x: 24, y: 74, d: 1.8 }, { e: '🍃', x: 90, y: 46, d: 2.4 }, { e: '💔', x: 6, y: 50, d: 3 },
];

const DOORS = [
  { to: '/let-go', icon: Wind, label: 'Let it go' },
  { to: '/sky-of-thoughts', icon: Sparkles, label: 'Read the Sky' },
  { to: '/', icon: MessageCircle, label: 'Talk to someone' },
];

export default function NotFound({ footer }: { footer?: React.ReactNode }) {
  useEffect(() => {
    const prev = document.title;
    document.title = 'This page drifted away | Zuzu Club';
    const robots = document.querySelector('meta[name="robots"]');
    const prevRobots = robots?.getAttribute('content');
    robots?.setAttribute('content', 'noindex, follow');
    return () => { document.title = prev; if (prevRobots) robots?.setAttribute('content', prevRobots); };
  }, []);

  return (
    <main id="main-content" className="relative h-[100dvh] overflow-y-auto bg-black text-white">
      <div className="fixed inset-0" aria-hidden="true">
        <StarrySky className="absolute inset-0" />
        {DRIFT.map(({ e, x, y, d }) => (
          <motion.span
            key={e + x}
            className="absolute text-3xl md:text-4xl select-none"
            style={{ left: `${x}%`, top: `${y}%`, filter: 'drop-shadow(0 0 14px rgba(242,193,78,.35))' }}
            initial={{ opacity: 0 }}
            animate={{ opacity: [0.35, 0.8, 0.35], y: [0, -14, 0], x: [0, 8, 0] }}
            transition={{ duration: 9 + d, delay: d, repeat: Infinity, ease: 'easeInOut' }}
          >
            {e}
          </motion.span>
        ))}
      </div>

      <div className="relative min-h-full flex flex-col px-4">
      <div className="flex-1 w-full max-w-xl mx-auto px-2 py-10 flex flex-col justify-center text-center">
        <Link to="/" className="text-3xl" style={{ fontFamily: "'Instrument Serif', serif" }} aria-label="Zuzu home">Zuzu</Link>
        <p className="mt-8 text-[0.625rem] uppercase tracking-[0.3em] text-white/55">404 · Lost in the sky</p>
        <h1 className="mt-3 text-4xl md:text-6xl leading-[1.05] font-normal" style={{ fontFamily: "'Instrument Serif', serif", textWrap: 'balance' }}>
          This page drifted away, <em className="text-[#f6e3b4]">like a thought you let go.</em>
        </h1>
        <p className="mt-5 text-base md:text-lg leading-relaxed text-white/75">
          Maybe the link was old. But you found your way to Zuzu, and that might be the part that matters.
        </p>
        <div className="mt-8 flex flex-wrap justify-center gap-2.5">
          {DOORS.map(({ to, icon: Icon, label }, i) => (
            <Link
              key={to + label}
              to={to}
              className={`inline-flex items-center gap-2 px-5 py-3 rounded-full text-xs font-semibold tracking-wide transition-colors ${
                i === 0 ? 'bg-white text-black hover:bg-white/90' : 'border border-white/20 bg-white/[0.06] backdrop-blur-md hover:bg-white/10'
              }`}
            >
              <Icon size={14} strokeWidth={1.75} aria-hidden="true" /> {label}
            </Link>
          ))}
        </div>
        <p className="mt-6 text-xs text-white/55">
          Or <Link to="/sky-of-thoughts/feelings" className="underline underline-offset-4 hover:text-white">find your feeling</Link> · <Link to="/faq" className="underline underline-offset-4 hover:text-white">FAQ</Link>
        </p>
      </div>
      {footer}
      </div>
    </main>
  );
}
